import React, { useEffect, useState } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Header from "@/components/layout/Header";
import Wizard from "@/components/wizard/Wizard";
import ExpertMode from "@/components/expert/ExpertMode";
import ComparisonTable from "@/components/comparison/ComparisonTable";
import Projects from "@/components/projects/Projects";
import LandingPage from "@/components/landing/LandingPage";
import CareComparison from "@/components/care/CareComparison";
import ConfigurationMode from "@/components/configurator/ConfigurationMode";
import ContactDialog from "@/components/contact/ContactDialog";
import ImpressumDialog from "@/components/legal/ImpressumDialog";
import CookieConsent from "@/components/legal/CookieConsent";
import { fetchFeatures } from "@/lib/api";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

function App() {
  const [features, setFeatures] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("landing"); // 'landing' | 'app'
  const [activeTab, setActiveTab] = useState("assistent");
  const [loadedProject, setLoadedProject] = useState(null);
  const [loadedConfigProject, setLoadedConfigProject] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactContext, setContactContext] = useState(null);
  const [impressumOpen, setImpressumOpen] = useState(false);

  const openContact = (ctx = null) => {
    setContactContext(ctx);
    setContactOpen(true);
  };

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchFeatures();
        setFeatures(data);
      } catch (e) {
        console.error(e);
        toast.error("Produktdaten konnten nicht geladen werden.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleLoadProject = (project) => {
    if (project?.mode === "configurator") {
      setLoadedConfigProject(project);
      setActiveTab("konfigurator");
      setView("app");
      toast.success(`Konfiguration „${project.name}“ geladen.`);
      return;
    }
    setLoadedProject(project);
    setActiveTab("assistent");
    setView("app");
    toast.success(`Projekt „${project.name}“ geladen.`);
  };

  const openInConfigurator = (project) => {
    setLoadedConfigProject({ ...project, mode: "configurator" });
    setActiveTab("konfigurator");
    setView("app");
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 30);
  };

  const goToApp = (tab = "assistent") => {
    setActiveTab(tab);
    setView("app");
    // scroll to top for a clean transition
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 30);
  };

  const goHome = () => {
    setView("landing");
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 30);
  };

  return (
    <div className="App min-h-screen">
      <div
        className="xortec-wave-bg"
        aria-hidden="true"
        style={{ backgroundImage: 'url("/xortec/bluewave.webp")' }}
      />
      <div className="xortec-wave-overlay" aria-hidden="true" />
      <Header onHome={goHome} onContact={() => openContact(null)} />
      <AnimatePresence mode="wait">
        {view === "landing" ? (
          <motion.main
            key="landing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="max-w-[1400px] mx-auto px-4 md:px-8 py-6"
          >
            <LandingPage
              onStart={() => goToApp("assistent")}
              onExpert={() => goToApp("expert")}
              onCompare={() => goToApp("vergleich")}
              onContact={() => openContact(null)}
              onCare={() => goToApp("care")}
              onConfigurator={() => goToApp("konfigurator")}
            />
          </motion.main>
        ) : (
          <motion.main
            key="app"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="max-w-[1400px] mx-auto px-4 md:px-8 py-6"
          >
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList
                data-testid="main-tabs"
                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 bg-secondary/60 border border-border p-1 rounded-xl h-auto"
              >
                <TabsTrigger data-testid="tab-assistent" value="assistent" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-sm md:text-base py-2">
                  Assistent
                </TabsTrigger>
                <TabsTrigger data-testid="tab-expert" value="expert" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-sm md:text-base py-2">
                  Expertenmodus
                </TabsTrigger>
                <TabsTrigger data-testid="tab-konfigurator" value="konfigurator" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-sm md:text-base py-2">
                  Konfigurator
                </TabsTrigger>
                <TabsTrigger data-testid="tab-comparison" value="vergleich" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-sm md:text-base py-2">
                  Vergleich
                </TabsTrigger>
                <TabsTrigger data-testid="tab-care" value="care" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-sm md:text-base py-2">
                  Care-Pakete
                </TabsTrigger>
                <TabsTrigger data-testid="tab-projects" value="projekte" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-sm md:text-base py-2">
                  Projekte
                </TabsTrigger>
              </TabsList>

              <div className="mt-6">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                >
                  <TabsContent value="assistent" forceMount hidden={activeTab !== "assistent"}>
                    <Wizard
                      features={features}
                      loading={loading}
                      initialProject={loadedProject}
                      onProjectConsumed={() => setLoadedProject(null)}
                      onContact={openContact}
                      onOpenInConfigurator={openInConfigurator}
                    />
                  </TabsContent>
                  <TabsContent value="expert" forceMount hidden={activeTab !== "expert"}>
                    <ExpertMode features={features} loading={loading} onOpenInConfigurator={openInConfigurator} />
                  </TabsContent>
                  <TabsContent value="konfigurator" forceMount hidden={activeTab !== "konfigurator"}>
                    <ConfigurationMode
                      initialProject={loadedConfigProject}
                      onProjectConsumed={() => setLoadedConfigProject(null)}
                      onContact={openContact}
                    />
                  </TabsContent>
                  <TabsContent value="vergleich" forceMount hidden={activeTab !== "vergleich"}>
                    <ComparisonTable features={features} loading={loading} />
                  </TabsContent>
                  <TabsContent value="care" forceMount hidden={activeTab !== "care"}>
                    <CareComparison onGoToWizard={() => goToApp("assistent")} />
                  </TabsContent>
                  <TabsContent value="projekte" forceMount hidden={activeTab !== "projekte"}>
                    <Projects onLoad={handleLoadProject} features={features} />
                  </TabsContent>
                </motion.div>
              </div>
            </Tabs>
          </motion.main>
        )}
      </AnimatePresence>

      <Toaster theme="dark" richColors position="top-right" />

      <ContactDialog open={contactOpen} onOpenChange={setContactOpen} context={contactContext} />
      <ImpressumDialog open={impressumOpen} onOpenChange={setImpressumOpen} />
      <CookieConsent />

      <footer className="border-t border-border mt-8">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-6 flex flex-wrap items-center justify-between gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-3">
            <img src="/xortec/xicon-white.webp" alt="Xortec" className="w-6 h-6" />
            <span>© {new Date().getFullYear()} Xortec GmbH – Distribution mit Lösungskompetenz</span>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => openContact(null)} className="hover:text-primary" data-testid="footer-contact">
              Kontakt
            </button>
            <a
              href="https://xortec.de/Rechtliches/AGB/"
              target="_blank"
              rel="noreferrer"
              className="hover:text-primary"
              data-testid="footer-agb"
            >
              AGB
            </a>
            <a
              href="https://xortec.de/Rechtliches/Datenschutz/"
              target="_blank"
              rel="noreferrer"
              className="hover:text-primary"
              data-testid="footer-datenschutz"
            >
              Datenschutz
            </a>
            <button
              onClick={() => setImpressumOpen(true)}
              className="hover:text-primary"
              data-testid="footer-impressum"
            >
              Impressum
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
